/**
 * Today Snapshot Service
 * 
 * Fast precomputed read for today's summary data
 * Uses localStorage for instant access, refreshes in background
 * 
 * This enables immediate UI rendering with cached data,
 * then background refresh updates with fresh data.
 */

import { getTodayDate } from '../utils/date';
import type { DailySummary } from '../hooks/useDailySummary';

const SNAPSHOT_STORAGE_KEY = 'coretrak.todaySnapshot';
const SNAPSHOT_VERSION = 1; // Increment when snapshot structure changes

interface TodaySnapshot {
  version: number;
  date: string; // YYYY-MM-DD
  timestamp: number; // Unix timestamp when snapshot was created
  data: DailySummary;
}

/**
 * Save today's snapshot to localStorage
 */
export function saveTodaySnapshot(summary: DailySummary): void {
  try {
    const snapshot: TodaySnapshot = {
      version: SNAPSHOT_VERSION,
      date: getTodayDate(),
      timestamp: Date.now(),
      data: summary,
    };
    
    localStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
    console.log('[TodaySnapshot] Saved snapshot to localStorage:', {
      date: snapshot.date,
      timestamp: snapshot.timestamp,
      hasData: !!(summary.tdee || summary.nutrition || summary.dailyBurn),
    });
  } catch (error) {
    console.warn('[TodaySnapshot] Failed to save snapshot:', error);
  }
}

/**
 * Load today's snapshot from localStorage
 * Returns null if snapshot is missing, stale, or invalid
 */
export function loadTodaySnapshot(): DailySummary | null {
  try {
    const stored = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    
    const snapshot: TodaySnapshot = JSON.parse(stored);
    
    // Validate snapshot
    if (snapshot.version !== SNAPSHOT_VERSION) {
      console.log('[TodaySnapshot] Snapshot version mismatch, ignoring:', {
        storedVersion: snapshot.version,
        expectedVersion: SNAPSHOT_VERSION,
      });
      return null;
    }
    
    const today = getTodayDate();
    if (snapshot.date !== today) {
      console.log('[TodaySnapshot] Snapshot is for different date, ignoring:', {
        snapshotDate: snapshot.date,
        today,
      });
      return null;
    }
    
    // Snapshot is valid - return cached data
    
    return snapshot.data;
  } catch (error) {
    console.warn('[TodaySnapshot] Failed to load snapshot:', error);
    return null;
  }
}

/**
 * Clear today's snapshot (e.g., on logout or cache invalidation)
 */
export function clearTodaySnapshot(): void {
  try {
    localStorage.removeItem(SNAPSHOT_STORAGE_KEY);
    console.log('[TodaySnapshot] Cleared snapshot from localStorage');
  } catch (error) {
    console.warn('[TodaySnapshot] Failed to clear snapshot:', error);
  }
}

/**
 * Check if snapshot exists and is valid for today
 */
export function hasValidTodaySnapshot(): boolean {
  const snapshot = loadTodaySnapshot();
  return snapshot !== null;
}

